import { DebugKey, createDebug } from '@ps/web-core-utils';
import { cloneDeep } from 'lodash';
import { Machine, assign, interpret, send } from 'xstate';

import type { MachineConfig, MachineOptions, State, WizardContext } from '@swf/core/types/Wizard';
import { Action, Event } from '@swf/core/types/Wizard';

import type { Node } from './Flow';

export interface FieldsMap {
  pageIdRef: string;
  fields: string[];
}

const getContext = (cmsHeaderAndGridLayout: boolean) => ({
  values: {},
  data: {
    fieldsMap: [] as FieldsMap[],
    cmsHeaderAndGridLayout
  },
  error: null
});

export const wizardName = 'wizard';
const wizardDebug = createDebug(DebugKey.Wizard);

export const defaultConfig = (cmsHeaderAndGridLayout?: boolean) => ({
  context: getContext(!!cmsHeaderAndGridLayout),
  id: wizardName,
  initial: 'idle',
  states: {
    idle: {
      on: {
        [Event.NEXT]: wizardName
      }
    }
  }
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getMeta = (value: any) =>
  value
    ? Object.keys(value).reduce(
        (result, key) => ({
          ...result,
          ...value[key]
        }),
        {}
      )
    : {};

export const createConfig = ({ config }: Node, cmsHeaderAndGridLayout?: boolean) => {
  const machineConfig = cloneDeep(defaultConfig(cmsHeaderAndGridLayout));
  machineConfig.states = {
    ...machineConfig.states,
    ...config
  };

  return machineConfig;
};

export const create = (config: MachineConfig, options: MachineOptions) => {
  wizardDebug('machine %o', config);

  // TODO: fix deprecated
  /* eslint-disable @typescript-eslint/no-explicit-any */
  return Machine<WizardContext>(config, {
    ...options,
    actions: {
      ...options.actions,
      [Action.DataAssign]: assign<any>({
        data: ({ data: contextData }: { data: any }, { data: eventData }: { data: any }) => ({
          ...contextData,
          ...eventData
        })
      }),
      [Action.ValuesAssign]: assign<any>({
        values: ({ values }: any, { data }: any) => ({
          ...values,
          ...data
        })
      }),
      [Action.GoNext]: send(Event.NEXT),
      [Action.GoJump]: send(Event.JUMP),
      [Action.GoPrevious]: send(Event.PREVIOUS),
      [Action.ErrorAssign]: assign((current: any, { data }: any) => {
        const { globalErrors, fieldErrors, message: defaultMessage } = data?.body ?? {};
        const { statusCode } = data?.headers ?? {};
        return {
          error: defaultMessage
            ? {
                globalErrors: [
                  {
                    defaultMessage,
                    code: statusCode.toString(),
                    arguments: null
                  }
                ]
              }
            : { globalErrors, fieldErrors }
        };
      }),
      [Action.ErrorReset]: assign<any>({
        error: null
      })
    }
  });
};

export const createInterpreter = (config: MachineConfig, options: MachineOptions) =>
  interpret(create(config, options));

export const run = (node: Node) =>
  new Promise<State>((resolve) => {
    const machineConfig = createConfig(node);
    const interpreter = createInterpreter(machineConfig, node.options);

    interpreter.onTransition((currentState) => {
      const { navigate } = getMeta(currentState.meta) as { navigate?: boolean };

      if (navigate) {
        resolve(currentState);
      }
    });

    interpreter.start();
    interpreter.send(Event.NEXT);
  });
